import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { activitiesAPI } from '../../services/api';
import { errorHandler } from '../../utils/errorHandler';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import './ActivityPage.css';

function ActivityPage() {
    const [activity, setActivity] = useState(null);
    const [selectedImage, setSelectedImage] = useState(0);
    const [imageArray, setImageArray] = useState([]);
    const { activityId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [retryCount, setRetryCount] = useState(0);

    useEffect(() => {
        if (!activityId) {
            setError('Activity ID is missing or invalid.');
            setLoading(false);
            return;
        }
        fetchActivity();
    }, [activityId]);

    const fetchActivity = async () => {
        try {
            setLoading(true);
            setError(null);
            if (!activityId) {
                throw new Error('Activity ID is missing or invalid');
            }
            const activityResponse = await activitiesAPI.getActivity(activityId);
            if (activityResponse) {
                setActivity(activityResponse);
                handleImageArray(activityResponse);
            } else {
                throw new Error('No activity data received');
            }
        } catch (error) {
            setError(errorHandler.processError(error).message);
            setActivity(null);
            setRetryCount(prev => prev + 1);
        } finally {
            setLoading(false);
        }
    };

    // Retry fetching if we have an error
    useEffect(() => {
        if (error && retryCount < 3) {
            const timer = setTimeout(() => {
                fetchActivity();
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [error, retryCount]);

    const handleImageArray = (activityData) => {
        if (activityData.images) {
            setImageArray(activityData.images);
        } else if (activityData.image) {
            setImageArray([activityData.image]);
        } else {
            setImageArray([]);
        }
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return (
            <div className="error-message">
                <p>{error}</p>
                {retryCount < 3 && <p>Retrying in 5 seconds...</p>}
                <button 
                    onClick={() => {
                        setRetryCount(0);
                        fetchActivity();
                    }}
                    className="retry-button"
                >
                    Retry Now
                </button>
            </div>
        );
    }

    if (!activity) {
        return <div>Activity not found</div>;
    }

    return (
        <div className="activity-page">
            <div className="hero-section">
                <div 
                    className="hero-image" 
                    style={{ backgroundImage: `url(${imageArray[0] || '/default-activity.jpg'})` }}
                >
                    <div className="hero-overlay">
                        <h1>{activity.title}</h1>
                        <div className="hero-metadata">
                            <div className="metadata-item">
                                <i className="fas fa-map-marker-alt"></i>
                                <span>
                                    {activity.location}
                                    {activity.state && `, ${activity.state}`}
                                    {activity.country && `, ${activity.country}`}
                                </span>
                            </div>
                            <div className="metadata-item">
                                <i className="fas fa-calendar-alt"></i>
                                <span>{new Date(activity.date).toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                })}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content-section">
                <div className="activity-description">
                    <h2>About this Activity</h2>
                    <p>{activity.description}</p>
                </div>

                {imageArray.length > 0 && (
                    <div className="gallery-section">
                        <h2>Photo Gallery</h2>
                        <div className="gallery-main">
                            <img 
                                src={imageArray[selectedImage]} 
                                alt={`${activity.title} - Featured`}
                                className="featured-image"
                            />
                        </div>
                        
                        {imageArray.length > 1 && (
                            <div className="gallery-thumbnails">
                                {imageArray.map((img, index) => (
                                    <div 
                                        key={index}
                                        className={`thumbnail ${selectedImage === index ? 'active' : ''}`}
                                        onClick={() => setSelectedImage(index)}
                                    >
                                        <img src={img} alt={`${activity.title} - ${index + 1}`} />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                <div className="back-button-container">
                    <button 
                        className="back-button"
                        onClick={() => navigate('/activities')}
                    >
                        <i className="fas fa-arrow-left"></i> Back to Activities
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ActivityPage;
 